import {
  adminApiClient,
  extractDownloadFilename,
  resolveApiErrorMessage,
  resolveBlobApiErrorMessage,
} from '@/services/admin/baseService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildModeAwareConfig } from '@/services/admin/modeAware'
import type {
  ApiResponse,
  BlocklistEntry,
  BlocklistImportResponse,
  BlocklistListResponse,
  BlocklistType,
  BlocklistMatchType,
  BlocklistChainCompatibility,
} from '@/types'

export interface BlocklistListParams {
  type?: BlocklistType
  isActived?: boolean
  limit?: number
  offset?: number
}

export interface BlocklistCreatePayload {
  type: BlocklistType
  matchType?: BlocklistMatchType
  value: string
  chainCompatibility?: BlocklistChainCompatibility | null
  isActived?: boolean
  reason?: string | null
}

export interface BlocklistUpdatePayload {
  matchType?: BlocklistMatchType
  value?: string
  chainCompatibility?: BlocklistChainCompatibility | null
  isActived?: boolean
  reason?: string | null
}

export interface BlocklistImportPayload {
  type: BlocklistType
  values: string
  matchType?: BlocklistMatchType
  chainCompatibility?: BlocklistChainCompatibility | null
  isActived?: boolean
  reason?: string | null
}

export const blocklistService = {
  async list(
    params?: BlocklistListParams,
    mode?: NetworkEnvironment
  ): Promise<BlocklistListResponse> {
    const response = await adminApiClient.get<ApiResponse<BlocklistListResponse>>(
      '/admin/system/blocklist',
      {
        params,
        ...buildModeAwareConfig(mode),
      }
    )

    return (
      response?.data?.data ?? { entries: [], total: 0, limit: 100, offset: 0 }
    )
  },

  async create(
    payload: BlocklistCreatePayload,
    mode?: NetworkEnvironment
  ): Promise<BlocklistEntry> {
    try {
      const response = await adminApiClient.post<ApiResponse<BlocklistEntry>>(
        '/admin/system/blocklist',
        payload,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to create blocklist entry')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to create blocklist entry'))
    }
  },

  async update(
    entryId: string,
    payload: BlocklistUpdatePayload,
    mode?: NetworkEnvironment
  ): Promise<BlocklistEntry> {
    try {
      const response = await adminApiClient.patch<ApiResponse<BlocklistEntry>>(
        `/admin/system/blocklist/${entryId}`,
        payload,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to update blocklist entry')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to update blocklist entry'))
    }
  },

  async delete(
    entryId: string,
    mode?: NetworkEnvironment
  ): Promise<BlocklistEntry> {
    try {
      const response = await adminApiClient.delete<ApiResponse<BlocklistEntry>>(
        `/admin/system/blocklist/${encodeURIComponent(entryId)}`,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to delete blocklist entry')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to delete blocklist entry'))
    }
  },

  async import(
    payload: BlocklistImportPayload,
    mode?: NetworkEnvironment
  ): Promise<BlocklistImportResponse> {
    try {
      const response = await adminApiClient.post<ApiResponse<BlocklistImportResponse>>(
        '/admin/system/blocklist/import',
        payload,
        buildModeAwareConfig(mode)
      )
      if (!response?.data?.data) {
        throw new Error('Failed to import blocklist entries')
      }
      return response.data.data
    } catch (error) {
      throw new Error(resolveApiErrorMessage(error, 'Failed to import blocklist entries'))
    }
  },

  async exportCsv(
    params?: BlocklistListParams,
    mode?: NetworkEnvironment
  ): Promise<{ blob: Blob; filename: string }> {
    try {
      const response = await adminApiClient.get<Blob>(
        '/admin/system/blocklist/export',
        {
          params,
          responseType: 'blob',
          ...buildModeAwareConfig(mode),
        }
      )
      return {
        blob: response.data,
        filename: extractDownloadFilename(
          response.headers['content-disposition'],
          `blocklist-${new Date().toISOString().slice(0, 10)}.csv`
        ),
      }
    } catch (error) {
      throw new Error(await resolveBlobApiErrorMessage(error, 'Failed to export blocklist entries'))
    }
  },
}
